import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';
import type {
  ClientCancellationLineDto,
  ClientCancellationLineQueueItemDto,
  ClientCancellationLineQueueQuery,
  ClientCancellationSuspensionBlockStatusDto,
  CreateClientCancellationLineRequest,
  PatchClientCancellationLineStatusRequest,
} from '../../types/clientCancellationLines';
import {
  normalizarClientCancellationLine,
  normalizarClientCancellationLines,
  normalizarClientCancellationQueue,
  normalizarSuspensionBlockStatus,
} from '../../utils/clientCancellationLines/clientCancellationLinesNormalize';
import {
  clientCancellationLinesMockStore,
  clientCancellationLinesUseMock,
} from './clientCancellationLinesMockStore';

const BASE = '/client-cancellation-lines';

class ClientCancellationLineService extends BaseApiService {
  private useMock = clientCancellationLinesUseMock();

  setUseMock(enabled: boolean) {
    this.useMock = enabled;
  }

  async getSuspensionBlockStatus(): Promise<ClientCancellationSuspensionBlockStatusDto> {
    if (this.useMock) return clientCancellationLinesMockStore.getSuspensionBlockStatus();
    const response = await this.api.get<ApiResponse<ClientCancellationSuspensionBlockStatusDto>>(
      `${BASE}/suspension-block-status`
    );
    return normalizarSuspensionBlockStatus(this.extractData(response));
  }

  async listByContract(contractId: string): Promise<ClientCancellationLineDto[]> {
    if (this.useMock) return clientCancellationLinesMockStore.listByContract(contractId);
    const response = await this.api.get<ApiResponse<ClientCancellationLineDto[]>>(
      `${BASE}/contracts/${encodeURIComponent(contractId)}`
    );
    return normalizarClientCancellationLines(this.extractData(response));
  }

  async create(
    contractId: string,
    payload: CreateClientCancellationLineRequest
  ): Promise<ClientCancellationLineDto> {
    if (this.useMock) {
      return clientCancellationLinesMockStore.create(contractId, payload);
    }
    const response = await this.api.post<ApiResponse<ClientCancellationLineDto>>(
      `${BASE}/contracts/${encodeURIComponent(contractId)}`,
      payload
    );
    return normalizarClientCancellationLine(this.extractData(response));
  }

  async cancelEntry(id: string): Promise<ClientCancellationLineDto> {
    if (this.useMock) return clientCancellationLinesMockStore.cancel(id);
    const response = await this.api.post<ApiResponse<ClientCancellationLineDto>>(
      `${BASE}/${encodeURIComponent(id)}/cancel`
    );
    return normalizarClientCancellationLine(this.extractData(response));
  }

  async getQueue(query: ClientCancellationLineQueueQuery): Promise<ClientCancellationLineQueueItemDto[]> {
    if (this.useMock) return clientCancellationLinesMockStore.queue(query);
    const response = await this.api.get<ApiResponse<ClientCancellationLineQueueItemDto[]>>(`${BASE}/queue`, {
      params: {
        referenceDate: query.referenceDate,
        includeOverdue: query.includeOverdue ?? false,
      },
    });
    return normalizarClientCancellationQueue(this.extractData(response));
  }

  async patchStatus(
    id: string,
    payload: PatchClientCancellationLineStatusRequest
  ): Promise<ClientCancellationLineDto> {
    if (this.useMock) return clientCancellationLinesMockStore.patchStatus(id, payload);
    const response = await this.api.patch<ApiResponse<ClientCancellationLineDto>>(
      `${BASE}/${encodeURIComponent(id)}/status`,
      payload
    );
    return normalizarClientCancellationLine(this.extractData(response));
  }
}

const clientCancellationLineService = new ClientCancellationLineService();
export default clientCancellationLineService;
